import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from "axios";
import type { ApiError, RefreshResponse } from "./types";

/**
 * アクセストークンは React 側の状態で持ち、axios インスタンスに注入する。
 * リフレッシュトークンは httpOnly cookie で持つのでここでは触らない。
 */
type TokenGetter = () => string | null;
type TokenSetter = (token: string | null) => void;

let getToken: TokenGetter = () => null;
let setToken: TokenSetter = () => {};
let onAuthFailure: () => void = () => {};

export function configureAuth(
  tokenGetter: TokenGetter,
  tokenSetter: TokenSetter,
  authFailure: () => void,
) {
  getToken = tokenGetter;
  setToken = tokenSetter;
  onAuthFailure = authFailure;
}

export const api: AxiosInstance = axios.create({
  baseURL: "/api/v1",
  withCredentials: true,
  timeout: 20000,
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 401 → refresh → リトライ。refresh 失敗時は onAuthFailure を呼ぶ。
let refreshing: Promise<string | null> | null = null;

async function refreshToken(): Promise<string | null> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    try {
      const resp = await axios.post<RefreshResponse>(
        "/api/v1/auth/refresh",
        {},
        { withCredentials: true },
      );
      const newToken = resp.data.access_token;
      setToken(newToken);
      return newToken;
    } catch {
      setToken(null);
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ error?: ApiError }>) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    const status = error.response?.status;
    const url = original?.url ?? "";

    if (
      status === 401 &&
      original &&
      !original._retry &&
      !url.includes("/auth/login") &&
      !url.includes("/auth/refresh")
    ) {
      original._retry = true;
      const newToken = await refreshToken();
      if (newToken && original.headers) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return api.request(original);
      }
      onAuthFailure();
    }

    return Promise.reject(error);
  },
);

/**
 * エラーレスポンスから表示用メッセージを取り出すヘルパ。
 */
export function apiErrorMessage(err: unknown, fallback = "エラーが発生しました"): string {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as { error?: ApiError } | undefined;
    if (body?.error?.message) return body.error.message;
    if (err.message) return err.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export function apiErrorCode(err: unknown): string | null {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as { error?: ApiError } | undefined;
    return body?.error?.code ?? null;
  }
  return null;
}
